import { RootStore } from "./rootStore";
import { observable, action, runInAction, makeAutoObservable } from "mobx";
import agent from "../api/agent";
import { toast } from "react-toastify";
import {
  ISupplier,
  ITributaryInformation,
  ITestSetStatus,
  IIntegration,
  IEmailConfig,
} from "../models/company";
import { history } from "../..";
import { TestSetStatusEnum } from "../util/GeneralEnums";
import { showError } from "../util/errorTypeEnumsOptions";

export default class CompanyStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  @observable resetTestSetStatus = false;
  @observable loadTestSet = false;
  @observable loadTestSetDocument = false;
  @observable loadingAddTestSetDocument = false;
  @observable loadingSendProdTestSetDocument = false;
  @observable loadSupplier = false;
  @observable testSetStatus: ITestSetStatus | null = null;
  @observable testSetDocuments = null;
  @observable btEditSupplier = false;
  @observable btTributarySupplier = false;
  @observable supplier: ISupplier | null = null;
  @observable image: string | null = null;
  @observable btUploadLogo = false;
  @observable btSupplierSw = false;
  @observable modalActionHistory = false;
  @observable btIntegrationSupplier = false;
  @observable btEmailConfigSupplier = false;

  @action openModal = () => {
    this.modalActionHistory = true;
  };
  @action closeModal = () => {
    this.modalActionHistory = false;
  };

  @action changeStatebtIntegrationSupplier = () => {
    this.btIntegrationSupplier = !this.btIntegrationSupplier;
  };

  @action changeStatebtEmailConfigSupplier = () => {
    this.btEmailConfigSupplier = !this.btEmailConfigSupplier;
  };

  @action changeStatebtSupplierSw = () => {
    this.btSupplierSw = !this.btSupplierSw;
  };
  @action changeStateloadTestSet = () => {
    this.loadTestSet = !this.loadTestSet;
  };
  @action changeStateloadSupplier = () => {
    this.loadSupplier = !this.loadSupplier;
  };
  @action changeStatebtEditSupplier = () => {
    this.btEditSupplier = !this.btEditSupplier;
  };
  @action changeStatebtTributarySupplier = () => {
    this.btTributarySupplier = !this.btTributarySupplier;
  };
  @action changeStatebtUploadLogo = () => {
    this.btUploadLogo = !this.btUploadLogo;
  };

  @action updateSupplierBillingInfo = async (values: ITributaryInformation) => {
    this.changeStatebtTributarySupplier();
    try {
      const res = await agent.Company.updateSupplierBillingInfo(values);
      toast.success("Los datos se han guardado correctamente");
      runInAction(() => {
        this.supplier = res;
      });
    } catch (error) {
      toast.error(
        "UPS! Ocurrio un error guardando información tributaria, vuelve a intentarlo más tarde"
      );
      showError(error);
    }
    this.changeStatebtTributarySupplier();
  };

  @action getImage = async () => {
    if (this.supplier?.logoUrl != null) {
      let url = this.supplier?.logoUrl || "";
      var myRequest = new Request(url);
      fetch(myRequest)
      .then((response) => {
        response.blob()
        .then((blob) => {
          const reader = new FileReader();
          reader.addEventListener("loadend", (event) => {
            var text = event.target?.result;
            runInAction(() => {
              this.image = "data:image/png;base64," + text;
            });
          });
          reader.readAsText(blob);
        });
      })
      .catch(() => {
        // TODO: fix error validation
      });
    }
  };

  @action updateSupplier = async (values: any) => {
    this.changeStatebtEditSupplier();
    try {
      const res = await agent.Company.updateSupplier(values);
      toast.success("Los datos se han guardado correctamente");
      runInAction(() => {
        this.supplier = res;
      });
    } catch (error) {
      showError(error);
    }
    this.changeStatebtEditSupplier();
  };

  @action updateIntegration = async (values: IIntegration) => {
    this.changeStatebtIntegrationSupplier();
    try {
      const res = await agent.Company.updateIntegration(values);
      toast.success("Los datos se han guardado correctamente");
      runInAction(() => {
        this.supplier = res;
      });
    } catch (error) {
      showError(error);
    }
    this.changeStatebtIntegrationSupplier();
  };

  @action updateEmailConfig = async (values: IEmailConfig) => {
    this.changeStatebtEmailConfigSupplier();
    try {
      const res = await agent.Company.updateEmailConfig(values);
      toast.success("Los datos se han guardado correctamente");
      runInAction(() => {
        this.supplier = res;
      });
    } catch (error) {
      showError(error);
    }
    this.changeStatebtEmailConfigSupplier();
  };

  @action updateSupplierSoftware = async (values: any) => {
    this.changeStatebtSupplierSw();
    try {
      const res = await agent.Company.updateSupplierSw(values);
      toast.success("Los datos se han guardado correctamente");
      runInAction(() => {
        this.supplier = res;
      });
    } catch (error) {
      showError(error);
    }
    this.changeStatebtSupplierSw();
  };

  @action uploadLogo = async (logo: Blob) => {
    if (logo != null) {
      try {
        this.changeStatebtUploadLogo();
        const res = await agent.Company.uploadLogo(logo);
        runInAction(() => {
          this.supplier = res;
          this.changeStatebtUploadLogo();
          toast.success("El logo se ha guardado correctamente");
          this.getImage();
        });
      } catch (error) {
        showError(error);
        this.changeStatebtUploadLogo();
      }
    } else toast.error("Debe seleccionar una imagen");
  };

  @action getSupplier = async () => {
    try {
      const company = await agent.Company.getSupplier();
      runInAction(() => {
        this.supplier = company;
        if (this.image == null) {
          this.getImage();
        }
        this.loadSupplier = true;
      });
    } catch (error) {
      showError(error);
    }
  };

  @action getTestSetStatus = async () => {
    if (!this.resetTestSetStatus) {
      try {
        const status = await agent.Company.getTestSetStatus();
        runInAction(() => {
          this.testSetStatus = status;
          this.loadTestSet = true;
        });
      } catch (error) {
        showError(error);
      }
    }
  };

  @action getTestSetDocuments = async () => {
    try {
      const status = await agent.Company.getTestSet();

      runInAction(() => {
        this.testSetDocuments = status;
        this.loadTestSetDocument = true;
      });
    } catch (error) {
      showError(error);
    }
  };

  @action addTestSetDocument = async (body) => {
    this.loadingAddTestSetDocument = true;
    try {
      await agent.Company.addTestSet(body);
      toast.success("Identificador del set de pruebas guardado correctamente");
      runInAction(() => {
        this.loadTestSetDocument = false;
        this.getTestSetDocuments();
      });
    } catch (error) {
      showError(error);
    } finally {
      this.loadingAddTestSetDocument = false;
    }
  };

  @action sendProdTestSetDocument = async (documentId) => {
    this.loadingSendProdTestSetDocument = true;
    try {
      await agent.Company.sendProdTestSet(documentId);
      toast.success("Identificador del set de pruebas habilitado en producción correctamente");
      runInAction(() => {
        this.loadingSendProdTestSetDocument = false;
        this.getTestSetDocuments();
      });
    } catch (error) {
      showError(error);
    } finally {
      this.loadingSendProdTestSetDocument = false;
    }
  };

  @action rebootTestSetDocument = async (body) => {
    try {
      await agent.Company.rebootTestSet(body);

      toast.success("Identificador del set de pruebas restablecido correctamente");

      runInAction(() => {
        this.getTestSetDocuments();
      });
    } catch (error) {
      showError(error);
    } finally {
      runInAction(() => {
        this.rootStore.modalStore.closeModal();
      });
    }
  };

  @action updateTestSet = async (testSetId: string) => {
    try {
      const supplier = await agent.Company.updateTestSet(testSetId);
      toast.success("Identidicador del set de pruebas guardado correctamente");
      runInAction(() => {
        this.resetTestSetStatus = false;
        this.supplier = supplier;
        this.loadTestSet = false;
        this.getTestSetStatus();
      });
    } catch (error) {
      showError(error);
    }
  };
  @action resetTestSet = async () => {
    try {
      runInAction(() => {
        this.testSetStatus.testSetId = "";
        this.testSetStatus.testSetStatus = TestSetStatusEnum.NoStart;
        this.testSetStatus.firstInvoiceName = "";
        this.resetTestSetStatus = true;
        this.rootStore.modalStore.closeModal();
      });
      history.push("/home/Configuration/SetDian");
    } catch (error) {
      showError(error);
    }
  };

  @action tesSetStatusApproved = async () => {
    try {
      const supplier = await agent.Company.tesSetStatusApproved();
      toast.success("Aprobado correctamente");
      runInAction(() => {
        this.supplier = supplier;
        this.getTestSetStatus();
      });
    } catch (error) {
      showError(error);
    }
  };
}
